// Teams and their membership.
// Route: /settings/teams

import { useEffect, useMemo, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  Toggle,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDate,
  titleCase,
  ActiveBadge,
} from "./shared";
import type { UserListResponse, UserRow } from "./users";

export interface TeamRow {
  id: string;
  name: string;
  description: string;
  assignment_strategy: string;
  is_active: boolean;
  member_count: number;
  created_at: string | null;
  updated_at: string | null;
}

interface TeamListResponse {
  teams: TeamRow[];
  total: number;
  page: number;
  limit: number;
}

export interface TeamMemberRow {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  is_available: boolean;
  last_assigned_at: string | null;
}

interface TeamMembersResponse {
  members: TeamMemberRow[];
}

interface TeamMemberResponse {
  member: TeamMemberRow;
}

// ============================================================ TeamsView

const STRATEGIES: { value: string; label: string; hint: string; glyph: string }[] = [
  {
    value: "round_robin",
    label: "Round robin",
    hint: "Distribute evenly to all team members",
    glyph: "↻",
  },
  {
    value: "load_balanced",
    label: "Load balanced",
    hint: "Assign to the agent with the fewest open conversations",
    glyph: "⚖",
  },
  { value: "manual", label: "Manual queue", hint: "Agents pick conversations up themselves", glyph: "✋" },
];

const strategyOf = (value: string) => STRATEGIES.find((s) => s.value === value);

interface TeamForm {
  name: string;
  description: string;
  assignment_strategy: string;
  is_active: boolean;
}

const EMPTY_TEAM_FORM: TeamForm = {
  name: "",
  description: "",
  assignment_strategy: "round_robin",
  is_active: true,
};

export function TeamsView() {
  const { can } = useSession();
  const toast = useToast();
  // The Vue original gated create/delete on the literal role name "admin".
  // These are the checks the API itself enforces, so a manager who genuinely
  // holds teams:write is no longer shown a button that would 403.
  const canWrite = can("teams", "write");
  const canDeleteTeam = can("teams", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<TeamListResponse>(
    () => http.get<TeamListResponse>(`/teams${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const teams = data?.teams ?? [];
  const total = data?.total ?? teams.length;

  const [users, setUsers] = useState<UserRow[]>([]);
  useEffect(() => {
    void http
      .get<UserListResponse>(`/users${qs({ limit: 100 })}`)
      .then((res) => setUsers(res.users ?? []))
      .catch(() => setUsers([]));
  }, []);

  const [editing, setEditing] = useState<TeamRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<TeamForm>(EMPTY_TEAM_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState<TeamRow | null>(null);

  // Members dialog.
  const [membersTeam, setMembersTeam] = useState<TeamRow | null>(null);
  const [members, setMembers] = useState<TeamMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<unknown>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_TEAM_FORM);
    setDialogOpen(true);
  };

  const openEdit = (team: TeamRow) => {
    setEditing(team);
    setForm({
      name: team.name,
      description: team.description ?? "",
      assignment_strategy: team.assignment_strategy || "round_robin",
      is_active: team.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.push("Enter a team name", "critical");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        // PUT overwrites description and is_active unconditionally, so the full
        // object goes every time — a partial body would clear them.
        await http.put(`/teams/${editing.id}`, {
          name: form.name.trim(),
          description: form.description,
          assignment_strategy: form.assignment_strategy,
          is_active: form.is_active,
        });
        toast.push("Team updated", "good");
      } else {
        await http.post("/teams", {
          name: form.name.trim(),
          description: form.description,
          assignment_strategy: form.assignment_strategy,
        });
        toast.push("Team created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the team"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/teams/${toDelete.id}`);
      toast.push("Team deleted", "good");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not delete the team"), "critical");
      throw e;
    }
  };

  const openMembers = async (team: TeamRow) => {
    setMembersTeam(team);
    setMembers([]);
    setMembersError(null);
    setMembersLoading(true);
    try {
      const res = await http.get<TeamMembersResponse>(`/teams/${team.id}/members`);
      setMembers(res.members ?? []);
    } catch (e) {
      setMembersError(e);
    } finally {
      setMembersLoading(false);
    }
  };

  const addMember = async (user: UserRow, role: "agent" | "manager") => {
    if (!membersTeam) return;
    try {
      const res = await http.post<TeamMemberResponse>(`/teams/${membersTeam.id}/members`, {
        user_id: user.id,
        role,
      });
      setMembers((list) => [...list, res.member]);
      toast.push(`${user.full_name || user.email} added`, "good");
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not add the member"), "critical");
    }
  };

  const removeMember = async (member: TeamMemberRow) => {
    if (!membersTeam) return;
    try {
      await http.del(`/teams/${membersTeam.id}/members/${member.user_id}`);
      setMembers((list) => list.filter((m) => m.user_id !== member.user_id));
      toast.push("Member removed", "good");
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not remove the member"), "critical");
    }
  };

  const availableUsers = useMemo(() => {
    const taken = new Set(members.map((m) => m.user_id));
    // Team membership drives chat assignment, which is scoped to the home
    // organisation — the API rejects a borrowed member, so they are not offered.
    return users.filter((u) => u.is_active && !u.is_member && !taken.has(u.id));
  }, [users, members]);

  const columns: Column<TeamRow>[] = [
    {
      key: "name",
      header: "Team",
      width: "250px",
      sortable: true,
      render: (t) => (
        <div>
          <div className="strong truncate">{t.name}</div>
          {t.description && <div className="muted truncate">{t.description}</div>}
        </div>
      ),
    },
    {
      key: "assignment_strategy",
      header: "Strategy",
      sortable: true,
      render: (t) => {
        const s = strategyOf(t.assignment_strategy);
        return (
          <span title={s?.hint}>
            <span aria-hidden>{s?.glyph ?? "↻"}</span> {s?.label ?? titleCase(t.assignment_strategy)}
          </span>
        );
      },
    },
    {
      key: "member_count",
      header: "Members",
      align: "right",
      sortable: true,
      render: (t) => (
        <button className="btn sm ghost" onClick={() => void openMembers(t)}>
          {t.member_count || 0}
        </button>
      ),
    },
    { key: "is_active", header: "Status", sortable: true, render: (t) => <ActiveBadge active={t.is_active} /> },
    { key: "created_at", header: "Created", sortable: true, render: (t) => fmtDate(t.created_at) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (t) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn icon sm ghost"
            title="Manage members"
            aria-label={`Manage members of ${t.name}`}
            onClick={() => void openMembers(t)}
          >
            ⊕
          </button>
          <button
            className="btn icon sm ghost"
            title="Edit this team"
            aria-label={`Edit ${t.name}`}
            disabled={!canWrite}
            onClick={() => openEdit(t)}
          >
            ✎
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete this team"
            aria-label={`Delete ${t.name}`}
            disabled={!canDeleteTeam}
            onClick={() => setToDelete(t)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Teams</h1>
          <div className="sub">Groups of agents, and how conversations reach them.</div>
        </div>
        <div className="page-actions">
          <button className="btn primary" disabled={!canWrite} onClick={openCreate}>
            + Add team
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the teams" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your teams</h3>
          <Search value={search} onChange={setSearch} placeholder="Search teams…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={teams}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching teams" : "No teams yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "A team routes incoming conversations to a group of agents by a strategy you choose."
                }
                icon="◍"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add team
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="teams"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit team" : "Create team"}
      >
        <Field label="Team name" required>
          <input
            value={form.name}
            placeholder="Claims triage"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Description">
          <textarea
            value={form.description}
            rows={2}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <Field
          label="Assignment strategy"
          hint={strategyOf(form.assignment_strategy)?.hint}
        >
          <select
            value={form.assignment_strategy}
            onChange={(e) => setForm({ ...form, assignment_strategy: e.target.value })}
          >
            {STRATEGIES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        {editing && (
          <div className="setting">
            <div className="text">
              <div className="strong">Team active</div>
              <p>An inactive team receives no new assignments.</p>
            </div>
            <Toggle
              checked={form.is_active}
              ariaLabel="Team active"
              onChange={(v) => setForm({ ...form, is_active: v })}
            />
          </div>
        )}
        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={submitting}>
            {submitting && <span className="spinner" />}
            {editing ? "Update team" : "Create team"}
          </button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={membersTeam !== null}
        onClose={() => setMembersTeam(null)}
        title={`Team members — ${membersTeam?.name ?? ""}`}
      >
        <ErrorBanner error={membersError} what="Could not load the members" />

        <div className="section">
          <h4>Current members ({members.length})</h4>
          {membersLoading ? (
            <div className="row">
              <span className="spinner" />
              <span className="muted">Loading members…</span>
            </div>
          ) : members.length === 0 ? (
            <p className="muted">No members yet.</p>
          ) : (
            <div className="col">
              {members.map((m) => (
                <div className="row" key={m.id}>
                  <div className="spacer">
                    <div className="strong truncate">{m.full_name || m.email || "Unknown user"}</div>
                    <div className="muted truncate">{m.email}</div>
                  </div>
                  <Badge>{titleCase(m.role)}</Badge>
                  <button
                    className="btn icon sm ghost"
                    title="Remove from team"
                    aria-label={`Remove ${m.full_name || m.email} from the team`}
                    onClick={() => void removeMember(m)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="section">
          <h4>Add members</h4>
          {availableUsers.length === 0 ? (
            <p className="muted">
              Every eligible user is already on this team. Members borrowed from another
              organisation cannot join a team — assignment is scoped to their home organisation.
            </p>
          ) : (
            <div className="col">
              {availableUsers.map((u) => (
                <div className="row" key={u.id}>
                  <div className="spacer">
                    <div className="strong truncate">{u.full_name || u.email}</div>
                    <div className="muted truncate">{u.email}</div>
                  </div>
                  <button className="btn sm" onClick={() => void addMember(u, "agent")}>
                    Add as agent
                  </button>
                  {canWrite && (
                    <button className="btn sm" onClick={() => void addMember(u, "manager")}>
                      Add as manager
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button className="btn" onClick={() => setMembersTeam(null)}>
            Close
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Delete team"
        body={
          <>
            <strong className="strong">{toDelete?.name}</strong> and its membership are deleted.
            Conversations already assigned stay with their agent. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
